import React, { useEffect, useState } from 'react';
import { Button, Card, Descriptions, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { CopyLine, fmtBytes, s3, toastErr } from './common';

const { Text } = Typography;
const GB = 1024 ** 3;
const METHODS = ['GET', 'HEAD', 'PUT', 'POST', 'DELETE'];
const tip = (title) => (
    <Tooltip title={title}><QuestionCircleOutlined style={{ color: 'var(--ant-color-text-tertiary, #999)', marginLeft: 6 }} /></Tooltip>
);

const emptyRule = () => ({ allowedOrigins: ['*'], allowedMethods: ['GET', 'HEAD'], allowedHeaders: [], exposeHeaders: ['ETag'], maxAgeSeconds: 3600 });

/** 输入需要完整桶名才可确认 */
const confirmByName = ({ t, name, title, okText, onOk }) => {
    let typed = '';
    let inst = null;
    const content = (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Text>{t('Type {{name}} to confirm', { name })}</Text>
            <Input autoFocus placeholder={name} onChange={(e) => {
                typed = e.target.value;
                inst?.update({ okButtonProps: { danger: true, disabled: typed !== name } });
            }} />
        </Space>
    );
    inst = Modal.confirm({
        title, content, okText, icon: null,
        okButtonProps: { danger: true, disabled: true },
        onOk: () => (typed === name ? onOk() : Promise.reject()),
    });
};

const DOMAIN_STATUS = {
    pending: ['default', 'Waiting for DNS'],
    issuing: ['processing', 'Issuing certificate'],
    active: ['success', 'Active'],
    error: ['error', 'Error'],
};

/** Custom domains (R2-style): CNAME the hostname to this bucket's target; we verify, add it to the CDN and issue a certificate. */
const CustomDomains = ({ bucketId, available }) => {
    const { t } = useTranslation();
    const [data, setData] = useState({ target: '', items: [] });
    const [host, setHost] = useState('');
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = React.useCallback(async () => {
        try {
            const d = await s3.get(`/buckets/${bucketId}/domains`);
            setData({ target: d?.target || '', items: Array.isArray(d?.items) ? d.items : [] });
        } catch (_) { /* card stays empty */ }
    }, [bucketId]);
    useEffect(() => { load(); }, [load]);
    // while something is waiting for DNS / a certificate, refresh on its own
    const waiting = data.items.some((d) => d.status === 'pending' || d.status === 'issuing');
    useEffect(() => {
        if (!waiting) return undefined;
        const h = setInterval(load, 15000);
        return () => clearInterval(h);
    }, [waiting, load]);

    const add = async () => {
        const v = host.trim();
        if (!v) return;
        setAdding(true);
        try {
            const r = await s3.post(`/buckets/${bucketId}/domains`, { hostname: v });
            setHost('');
            if (r?.dns_added) message.success(t('DNS record added for you'));
            load();
        } catch (e) { toastErr(e); } finally { setAdding(false); }
    };
    const check = async (d) => {
        setBusy(d.id);
        try {
            await s3.post(`/buckets/${bucketId}/domains/${d.id}/check`);
            load();
        } catch (e) { toastErr(e); } finally { setBusy(null); }
    };
    const remove = (d) => Modal.confirm({
        title: t('Remove {{name}}?', { name: d.hostname }),
        okText: t('Delete'),
        okButtonProps: { danger: true },
        onOk: async () => {
            try {
                await s3.del(`/buckets/${bucketId}/domains/${d.id}`);
                load();
            } catch (e) { toastErr(e); }
        },
    });

    return (
        <Card size="small" style={{ marginBottom: 16 }}
            title={<span>{t('Custom domains')}{tip(t('Serve this bucket publicly on your own domain, through our CDN with HTTPS'))}</span>}>
            {available === false ? <Text type="secondary">{t('Public access via CDN is coming soon')}</Text> : (
                <>
                    <Space.Compact style={{ width: '100%', maxWidth: 520, marginBottom: 12 }}>
                        <Input placeholder="files.example.com" value={host} onChange={(e) => setHost(e.target.value)} onPressEnter={add} />
                        <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={add}>{t('Add')}</Button>
                    </Space.Compact>
                    {data.items.length > 0 && (
                        <Table size="small" rowKey="id" pagination={false} dataSource={data.items} columns={[
                            { title: t('Domain'), dataIndex: 'hostname', render: (v, d) => (d.status === 'active' ? <a href={`https://${v}/`} target="_blank" rel="noopener noreferrer">{v}</a> : v) },
                            {
                                title: t('Status'), dataIndex: 'status', width: 200, render: (v, d) => (
                                    <Space direction="vertical" size={0}>
                                        <Tag color={DOMAIN_STATUS[v]?.[0]}>{t(DOMAIN_STATUS[v]?.[1] || v)}</Tag>
                                        {d.error && v !== 'pending' && <Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: d.error }}>{d.error}</Text>}
                                    </Space>
                                ),
                            },
                            {
                                title: '', key: 'a', width: 90, align: 'right', render: (_, d) => (
                                    <Space size={0}>
                                        {d.status !== 'active' && <Tooltip title={t('Check now')}><Button size="small" type="text" icon={<ReloadOutlined />} loading={busy === d.id} onClick={() => check(d)} /></Tooltip>}
                                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(d)} />
                                    </Space>
                                ),
                            },
                        ]} />
                    )}
                    {data.target && (data.items.length === 0 || data.items.some((d) => d.status === 'pending' || d.status === 'error')) && (
                        <div style={{ marginTop: 12 }}>
                            <Text type="secondary">{t('Add a CNAME record at your DNS provider pointing to')}</Text>
                            <CopyLine value={data.target} />
                            <Text type="secondary" style={{ fontSize: 12 }}>{t('If your DNS is on Cloudflare, turn the proxy off (DNS only)')}</Text>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
};

const CorsEditor = ({ bucketId, rules: initial, onSaved }) => {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setRules((Array.isArray(initial) ? initial : []).map((r, i) => ({ ...emptyRule(), ...r, _k: i })));
        setDirty(false);
    }, [initial]);

    const patch = (k, field, v) => {
        setRules((x) => x.map((r) => (r._k === k ? { ...r, [field]: v } : r)));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const clean = rules.map(({ _k, ...r }) => ({
                allowedOrigins: r.allowedOrigins || [],
                allowedMethods: r.allowedMethods || [],
                allowedHeaders: r.allowedHeaders || [],
                exposeHeaders: r.exposeHeaders || [],
                maxAgeSeconds: Number(r.maxAgeSeconds) || 0,
            }));
            await s3.put(`/buckets/${bucketId}/cors`, { rules: clean });
            message.success(t('Saved'));
            setDirty(false);
            onSaved?.();
        } catch (e) { toastErr(e); } finally { setSaving(false); }
    };

    const tags = (field, placeholder) => (_, r) => (
        <Select mode="tags" size="small" style={{ width: '100%' }} value={r[field] || []} placeholder={placeholder}
            tokenSeparators={[',', ' ']} open={false} suffixIcon={null} onChange={(v) => patch(r._k, field, v)} />
    );

    const cols = [
        { title: t('Allowed origins'), key: 'o', width: 240, render: tags('allowedOrigins', 'https://example.com') },
        {
            title: t('Methods'), key: 'm', width: 220,
            render: (_, r) => (
                <Select mode="multiple" size="small" style={{ width: '100%' }} value={r.allowedMethods || []}
                    options={METHODS.map((m) => ({ value: m, label: m }))} onChange={(v) => patch(r._k, 'allowedMethods', v)} />
            ),
        },
        { title: t('Allowed headers'), key: 'h', width: 180, render: tags('allowedHeaders', '*') },
        { title: t('Expose headers'), key: 'e', width: 160, render: tags('exposeHeaders', 'ETag') },
        {
            title: t('Max age (s)'), key: 'a', width: 110,
            render: (_, r) => <InputNumber size="small" min={0} max={86400} value={r.maxAgeSeconds} onChange={(v) => patch(r._k, 'maxAgeSeconds', v)} />,
        },
        {
            title: '', key: 'x', width: 44,
            render: (_, r) => <Button size="small" type="text" danger icon={<DeleteOutlined />}
                onClick={() => { setRules((x) => x.filter((y) => y._k !== r._k)); setDirty(true); }} />,
        },
    ];

    return (
        <Card size="small" style={{ marginBottom: 16 }}
            title={<span>{t('CORS')}{tip(t('Allows browsers on other sites to call this bucket directly'))}</span>}
            extra={<Space>
                <Button size="small" icon={<PlusOutlined />}
                    onClick={() => { setRules((x) => [...x, { ...emptyRule(), _k: Date.now() }]); setDirty(true); }}>{t('Add rule')}</Button>
                <Button size="small" type="primary" onClick={save} loading={saving} disabled={!dirty}>{t('Save')}</Button>
            </Space>}>
            <Table rowKey="_k" size="small" columns={cols} dataSource={rules} pagination={false} scroll={{ x: 960 }}
                locale={{ emptyText: t('No CORS rules') }} />
        </Card>
    );
};

const BucketSettings = ({ bucket, onChanged, onDeleted }) => {
    const { t } = useTranslation();
    const id = bucket?.id;
    const [pubSaving, setPubSaving] = useState(false);
    const [quota, setQuota] = useState(0);
    const [quotaSaving, setQuotaSaving] = useState(false);
    const [emptying, setEmptying] = useState(null);

    useEffect(() => {
        setQuota(bucket?.quota_bytes ? +(Number(bucket.quota_bytes) / GB).toFixed(2) : 0);
    }, [bucket?.quota_bytes]);

    const togglePublic = async (enabled) => {
        setPubSaving(true);
        try {
            await s3.put(`/buckets/${id}/public`, { enabled });
            onChanged?.();
        } catch (e) { toastErr(e); } finally { setPubSaving(false); }
    };

    const saveQuota = async () => {
        setQuotaSaving(true);
        try {
            await s3.put(`/buckets/${id}/quota`, { quota_gb: Number(quota) || 0 });
            message.success(t('Saved'));
            onChanged?.();
        } catch (e) { toastErr(e); } finally { setQuotaSaving(false); }
    };

    const runEmpty = async () => {
        let total = 0;
        setEmptying(0);
        try {
            for (let i = 0; i < 1000; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                const d = await s3.post(`/buckets/${id}/empty`);
                total += Number(d?.deleted) || 0;
                setEmptying(total);
                if (!d?.remaining) break;
            }
            message.success(t('Bucket emptied ({{count}} objects deleted)', { count: total }));
            onChanged?.();
        } catch (e) { toastErr(e); } finally { setEmptying(null); }
    };

    const doDelete = async () => {
        try {
            await s3.del(`/buckets/${id}`);
            message.success(t('Bucket deleted'));
            onDeleted?.();
        } catch (e) {
            if (e?.status === 409) {
                if (!e.toasted) message.error(t('Bucket is not empty. Empty it first.'));
            } else toastErr(e);
        }
    };

    const pathStyle = bucket?.endpoint ? `${String(bucket.endpoint).replace(/\/$/, '')}/${bucket.name}` : '';

    return (
        <div>
            <Card size="small" style={{ marginBottom: 16 }}
                title={<span>{t('Public access')}{tip(t('Anyone with the URL can read objects. Served through our CDN.'))}</span>}
                extra={bucket?.public_available === false && !bucket?.public_access
                    ? <Tooltip title={t('Public access via CDN is coming soon')}><Switch checked={false} disabled /></Tooltip>
                    : <Switch checked={!!bucket?.public_access} loading={pubSaving} onChange={togglePublic} />}>
                {bucket?.public_access
                    ? <CopyLine value={bucket.public_url} />
                    : <Text type="secondary">{t('Private — requests must be signed with an access key')}</Text>}
            </Card>

            <CustomDomains bucketId={id} available={bucket?.public_available} />

            <Card size="small" style={{ marginBottom: 16 }} title={t('Endpoint')}>
                <Descriptions column={1} size="small">
                    <Descriptions.Item label={t('Region')}>{bucket?.region_name} <Text code>{bucket?.region}</Text></Descriptions.Item>
                    <Descriptions.Item label={t('Path-style')}><CopyLine value={pathStyle} /></Descriptions.Item>
                    <Descriptions.Item label={t('Virtual-hosted')}><CopyLine value={bucket?.virtual_host} /></Descriptions.Item>
                </Descriptions>
            </Card>

            <CorsEditor bucketId={id} rules={bucket?.cors} onSaved={onChanged} />

            <Card size="small" style={{ marginBottom: 16 }}
                title={<span>{t('Quota')}{tip(t('Uploads are rejected once the bucket reaches this size. 0 = no limit'))}</span>}>
                <Space wrap>
                    <InputNumber min={0} step={1} value={quota} onChange={(v) => setQuota(v ?? 0)} addonAfter="GB" style={{ width: 180 }} />
                    <Button type="primary" onClick={saveQuota} loading={quotaSaving}>{t('Save')}</Button>
                    <Text type="secondary">{t('Used')}: {fmtBytes(bucket?.bytes)}</Text>
                </Space>
            </Card>

            <Card size="small" title={<Text type="danger">{t('Danger zone')}</Text>} style={{ borderColor: 'var(--ant-color-error-border, #ffccc7)' }}>
                <Space wrap>
                    <Button danger loading={emptying !== null}
                        onClick={() => confirmByName({ t, name: bucket?.name, title: t('Empty bucket'), okText: t('Empty bucket'), onOk: () => { runEmpty(); } })}>
                        {emptying !== null ? t('Emptying… {{count}} deleted', { count: emptying }) : t('Empty bucket')}
                    </Button>
                    <Button danger type="primary" disabled={emptying !== null}
                        onClick={() => confirmByName({ t, name: bucket?.name, title: t('Delete bucket'), okText: t('Delete'), onOk: doDelete })}>
                        {t('Delete bucket')}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

export default BucketSettings;
